import moment from 'moment';
import {kit} from './tokens';

// Shared, SYNCHRONOUS status model for the redesigned web app -- the web port
// of the mobile pure model (packages/mobile/src/common/design/rfpStatus.ts).
// It replaces the fragmented trio of useDisplayStatus /
// useVerticalDisplayStatus / BidStatusChip color logic, which disagree on
// FUNDED (blue "IN PROGRESS" vs green "Awarded") and only resolve inside a
// useEffect (a one-frame gray flash).
//
// FUNDED ruling: FUNDED renders GREEN (tone 'safe' -- the homeowner's money
// is held safely in escrow), matching the mobile kit token
// (--green: success / money-held-safe).

export type StatusTone =
  | 'neutral'
  | 'pending'
  | 'active'
  | 'safe'
  | 'success'
  | 'ai';

export interface RfpStatus {
  label: string;
  tone: StatusTone;
}

// Chip colors per tone: fg text on a tinted bg surface.
export const toneColors: Record<StatusTone, {fg: string; bg: string}> = {
  neutral: {fg: kit.slate, bg: kit.mist},
  pending: {fg: kit.amber, bg: kit.amberTint},
  active: {fg: kit.blueInk, bg: kit.blueTint},
  safe: {fg: kit.green, bg: kit.greenTint},
  success: {fg: kit.green, bg: kit.greenTint},
  ai: {fg: kit.purple, bg: kit.purpleTint},
};

// Minimal structural input. IRequestForProposal and IRequestForProposalPrivate
// (@bidmii/common IServerApi) both satisfy it, as do the loosely-typed list
// rows the old hooks were fed.
export interface RfpStatusInput {
  state?: string | null;
  type?: string | null;
  serviceOrderState?: string | null;
}

const DIRECT_QUOTE_TYPES = ['REQUESTED', 'BIDMII_DIRECT'];

const SERVICE_ORDER_ACTIVE_STATES = [
  'TO_BE_SCHEDULED',
  'SCHEDULED',
  'DISPATCHED',
  'IN_PROGRESS',
  'PENDING_APPROVAL',
  'NOT_APPROVED',
];

// Map an RFP (or Virtuo service order) to its display status. Pure and
// synchronous so chips never flash gray while a useEffect settles.
export const getRfpStatus = (rfp?: RfpStatusInput | null): RfpStatus => {
  // Virtuo / service orders never take quotes -- their own state machine.
  if (rfp?.type === 'SERVICE_ORDER') {
    if (rfp?.serviceOrderState === 'COMPLETE') {
      return {label: 'Complete', tone: 'success'};
    }
    if (rfp?.serviceOrderState === 'CANCELLED') {
      return {label: 'Cancelled', tone: 'neutral'};
    }
    if (SERVICE_ORDER_ACTIVE_STATES.includes(rfp?.serviceOrderState ?? '')) {
      return {label: 'Scheduled', tone: 'active'};
    }
    // DRAFT / PENDING / unknown -- still awaiting a schedule.
    return {label: 'Scheduling', tone: 'pending'};
  }

  switch (rfp?.state) {
    case 'DRAFT':
      return {label: 'Draft', tone: 'neutral'};
    case 'FUNDED':
      // Green, not blue -- see the FUNDED ruling above.
      return {label: 'Funded', tone: 'safe'};
    case 'COMPLETE':
      return {label: 'Complete', tone: 'success'};
    case 'CANCELLED':
      return {label: 'Cancelled', tone: 'neutral'};
    case 'EXPIRED':
      return {label: 'Closed (expired)', tone: 'neutral'};
    case 'OPEN':
    default:
      return DIRECT_QUOTE_TYPES.includes(rfp?.type ?? '')
        ? {label: 'Direct quote request', tone: 'ai'}
        : {label: 'Open for quotes', tone: 'pending'};
  }
};

// Contractor-side quote status -- the web port of the mobile kit's
// contractor status model (Track B, ported additively; getRfpStatus above is
// unchanged and still the only status source for the RFP itself). This is a
// SEPARATE pure function because "my quote's" status depends on both the
// proposal (state/expired) and its parent RFP (state) -- fields no
// RfpStatusInput consumer needed before My Quotes / My Jobs existed.
export interface ContractorQuoteStatusInput {
  // The contractor's own IProposal.state (e.g. 'DRAFT' | 'OPEN' | ...).
  proposalState?: string | null;
  // IProposal.expired -- true once the bid's expiryDate has passed unanswered.
  proposalExpired?: boolean | null;
  // True when this is the proposal that won the job (same test ContractorDialog
  // already uses: rfp.state in FUNDED/COMPLETE && the awarded proposal is owned
  // by my effective contractor -- ownsAsCompany(user, proposal.user.id), which
  // is just `proposal.user.id === me` for a normal standalone account and
  // resolves to the parent company for a sub-login).
  won?: boolean;
  // The parent RequestForProposal's state, to tell "still open" apart from
  // "awarded to someone else" and "cancelled".
  rfpState?: string | null;
}

export const getContractorQuoteStatus = (
  input: ContractorQuoteStatusInput,
): RfpStatus => {
  if (input.won) {
    return {label: 'Won', tone: 'success'};
  }
  if (input.rfpState === 'EXPIRED') {
    // The project hit its lifecycle limit and closed without a hire --
    // nobody won it, so "Not selected" would be misleading. Checked before
    // the proposal's own state because expiry flips open proposals to
    // CANCELLED server-side ("Cancelled" would read like a withdrawal).
    return {label: 'Project closed', tone: 'neutral'};
  }
  if (input.proposalExpired) {
    return {label: 'Expired', tone: 'neutral'};
  }
  // The proposal's own state -- set directly by BidStatusChip's existing
  // stateLookup (common/StatusChip.tsx) independent of the parent RFP's
  // state (a homeowner can decline one bid while the RFP stays OPEN).
  if (input.proposalState === 'DECLINED') {
    return {label: 'Not selected', tone: 'neutral'};
  }
  if (input.proposalState === 'CANCELLED') {
    return {label: 'Cancelled', tone: 'neutral'};
  }
  if (input.rfpState === 'CANCELLED') {
    // The homeowner pulled the job itself -- distinct from losing to another
    // contractor, so this gets its own label rather than "Not selected".
    return {label: 'Cancelled', tone: 'neutral'};
  }
  if (input.rfpState && ['FUNDED', 'COMPLETE'].includes(input.rfpState)) {
    // The job moved on without this proposal -- awarded elsewhere.
    return {label: 'Not selected', tone: 'neutral'};
  }
  if (input.proposalState === 'DRAFT') {
    return {label: 'Draft', tone: 'neutral'};
  }
  return {label: 'Quoted', tone: 'active'};
};

// "Closes in N days" countdown for an OPEN project carrying a lifecycle
// expiryDate (the 60-day project lifespan). Same moment-diff approach as
// rfpPresentation's getPromotedDaysLeft, but ceil'd so a project closing
// later today reads "Closes today" rather than a negative count. Returns
// null when there's nothing to count down (no expiryDate, or the project
// already left OPEN) so callers can omit the chip entirely.
export const getExpiryCountdown = (rfp?: {
  state?: string | null;
  expiryDate?: string | null;
}): RfpStatus | null => {
  if (rfp?.state !== 'OPEN' || !rfp?.expiryDate) {
    return null;
  }
  const days = Math.ceil(moment(rfp.expiryDate).diff(moment(), 'days', true));
  const label =
    days > 1
      ? `Closes in ${days} days`
      : days === 1
        ? 'Closes tomorrow'
        : 'Closes today';
  return {label, tone: days <= 7 ? 'pending' : 'neutral'};
};
